import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import * as XLSX from "xlsx";
import { analysisResultApi } from "../../common/api";
import { getDefaultColDef } from "../../common/constants/commonFunction";
import { layoutStyle } from "../layout-ui/style";
import { TruckLoader } from "./truckLoader";

export const UsageReport = () => {
  const classes = layoutStyle();
  const defaultColDef = useMemo(() => getDefaultColDef(), []);
  const [usageRows, setUsageRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [anchorElExport, setAnchorElExport] = useState(null);

  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setLoading(false);
      return;
    }

    let isCurrentRequest = true;
    const loadUsageReport = async () => {
      setLoading(true);
      try {
        const response = await analysisResultApi.getUsageReport({
          startDate: startDate || null,
          endDate: endDate || null,
        });
        if (isCurrentRequest) {
          setUsageRows(response?.data?.data || []);
        }
      } finally {
        if (isCurrentRequest) {
          setLoading(false);
        }
      }
    };

    loadUsageReport();

    return () => {
      isCurrentRequest = false;
    };
  }, [endDate, startDate]);

  const handleExportFile = (format) => {
    const exportData = usageRows.map((row) => ({
      Dealer: row.dealerName || "",
      Company: row.companyName || "",
      Report: row.reportName || "",
      "Date(MM/DD/YYYY)": row.createdAt
        ? moment(row.createdAt).format("MM/DD/YYYY")
        : "",
      Time: row.createdAt ? moment(row.createdAt).format("hh:mm:ss A") : "",
      User: row.userName || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Usage Report");
    XLSX.writeFile(
      workbook,
      format === "csv" ? "UsageReport.csv" : "UsageReport.xlsx",
    );
    setAnchorElExport(null);
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
  };

  return (
    <Box className={classes.mainContentCompany}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Box className={classes.headerNameText}>Usage Report</Box>
        <Box className={classes.addEditGrid}>
          <Button
            variant="outlined"
            className={[classes.fleetListNewButton, classes.fontSize12]}
            onClick={(event) => setAnchorElExport(event.currentTarget)}
            disabled={loading || !usageRows.length}
          >
            Export
          </Button>
          <Menu
            anchorEl={anchorElExport}
            open={Boolean(anchorElExport)}
            onClose={() => setAnchorElExport(null)}
          >
            <MenuItem onClick={() => handleExportFile("xlsx")}>
              Export XLSX
            </MenuItem>
            <MenuItem onClick={() => handleExportFile("csv")}>
              Export CSV
            </MenuItem>
          </Menu>
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
          mt: "6px",
          mb: "4px",
          padding: "6px 10px",
          backgroundColor: "#f5f7f5",
          borderRadius: "6px",
          border: "1px solid #dde8da",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: "#18333A",
            minWidth: "max-content",
          }}
        >
          Timeframe:
        </Typography>
        <TextField
          type="date"
          size="small"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          sx={{
            "& .MuiInputBase-input": { fontSize: "12px", padding: "6px 10px" },
            "& .MuiInputLabel-root": { fontSize: "12px" },
            "& .MuiOutlinedInput-root": { borderRadius: "4px" },
            minWidth: 150,
          }}
        />
        <Typography sx={{ fontSize: "12px", color: "#555" }}>to</Typography>
        <TextField
          type="date"
          size="small"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
          sx={{
            "& .MuiInputBase-input": { fontSize: "12px", padding: "6px 10px" },
            "& .MuiInputLabel-root": { fontSize: "12px" },
            "& .MuiOutlinedInput-root": { borderRadius: "4px" },
            minWidth: 150,
          }}
        />
        {(startDate || endDate) && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleClearDates}
            sx={{
              fontSize: "11px",
              textTransform: "none",
              color: "error.main",
              borderColor: "error.main",
              padding: "3px 10px",
            }}
          >
            Clear
          </Button>
        )}
        {(startDate || endDate) && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "#18333A",
              fontStyle: "italic",
              ml: 1,
            }}
          >
            {`Showing ${usageRows?.length ?? 0} record(s)`}
          </Typography>
        )}
      </Stack>
      <Box className={classes.gridContantDealer}>
        <Box
          sx={{
            height: "calc(100vh - 180px)",
            minHeight: 300,
            width: "100%",
          }}
        >
          {loading ? (
            <TruckLoader />
          ) : (
            <AgGridReact
              className="ag-theme-quartz"
              rowSelection="multiple"
              pagination={true}
              rowData={usageRows}
              defaultColDef={defaultColDef}
              columnDefs={[
                { field: "dealerName", headerName: "Dealer" },
                { field: "companyName", headerName: "Company" },
                { field: "reportName", headerName: "Report" },
                {
                  field: "createdAt",
                  headerName: "Date(MM/DD/YYYY)",
                  valueFormatter: (params) =>
                    params?.value
                      ? moment(params.value).format("MM/DD/YYYY")
                      : "",
                },
                {
                  field: "createdAt",
                  headerName: "Time",
                  valueFormatter: (params) =>
                    params?.value
                      ? moment(params.value).format("hh:mm:ss A")
                      : "",
                },
                { field: "userName", headerName: "User" },
              ]}
              suppressRowClickSelection={true}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
